import type * as React from 'react';
import type { InputContainerProps } from '../InputContainer/index.ts';
import { Field } from '@base-ui/react/field';
import { Select as BaseSelect } from '@base-ui/react/select';
import clsx from 'clsx';
import { LuChevronsUpDown } from 'react-icons/lu';
import { PiCheckBold } from 'react-icons/pi';
import { FormattedError } from '../formatted/index.ts';
import { InputContainer } from '../InputContainer/index.ts';
import { useTextInputGroup } from '../TextInput/index.ts';

type SelectRenderContext = {
  placement: 'option' | 'value';
};

export type SelectItem<TValue> = {
  content: React.ReactNode | ((context: SelectRenderContext) => React.ReactNode);
  disabled?: boolean;
  key?: React.Key;
  textValue?: string;
  value: TValue;
};

export type SelectProps<TValue> = {
  autoFocus?: boolean;
  disabled?: boolean;
  onChange?: (value: TValue) => void;
  isItemEqualToValue?: NonNullable<BaseSelect.Root.Props<TValue>['isItemEqualToValue']>;
  items: ReadonlyArray<SelectItem<TValue>>;
  placeholder?: string;
  rounded?: boolean;
  value?: TValue;
} & Pick<InputContainerProps, 'cornerText' | 'error' | 'helpText' | 'label'>;

export function Select<TValue>({
  autoFocus,
  cornerText,
  disabled,
  error,
  helpText,
  isItemEqualToValue = Object.is,
  items,
  label,
  onChange,
  placeholder,
  rounded,
  value
}: SelectProps<TValue>) {
  const inputGroup = useTextInputGroup();

  const fieldLabel = label ? (
    <BaseSelect.Label render={<span />}>{label}</BaseSelect.Label>
  ) : undefined;

  const fieldError =
    error && typeof error !== 'boolean' ? (
      <Field.Error render={<span />} match>
        <FormattedError error={error} />
      </Field.Error>
    ) : (
      error
    );

  const fieldHelpText = helpText ? (
    <Field.Description render={<span />}>{helpText}</Field.Description>
  ) : undefined;

  const selectedOptionClasses = clsx(
    // Basic layout
    'relative block w-full appearance-none py-[calc(--spacing(1.5)-1px)]',
    !inputGroup && clsx(rounded ? 'rounded-full' : 'rounded-md'),
    inputGroup?.isFirst && clsx(rounded ? 'rounded-l-full' : 'rounded-l-md'),
    inputGroup?.isLast && clsx(rounded ? 'rounded-r-full' : 'rounded-r-md'),

    // Set minimum height for when no value is selected
    'min-h-9',

    // Horizontal padding
    'pr-[calc(--spacing(9)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pr-[calc(--spacing(7)-1px)] sm:pl-[calc(--spacing(3)-1px)]',

    // Typography
    'text-left text-base/6 text-gray-950 sm:text-sm/6 forced-colors:text-[CanvasText]',

    // Border
    'border-gray-950/10 group-hover:border-gray-950/20 group-data-pressed:border-gray-950/20',
    !inputGroup && 'border-t border-b',
    !inputGroup?.isFirst && 'border-l',
    !inputGroup?.isLast && 'border-r',

    // Background color
    'bg-transparent',

    // Invalid state
    'group-data-invalid:border-red-500 group-data-invalid:group-hover:border-red-500',

    // Disabled state
    'group-data-disabled:border-gray-950/20 group-data-disabled:opacity-100'
  );

  return (
    <Field.Root disabled={disabled} invalid={!!error} render={<div className="contents" />}>
      <BaseSelect.Root<TValue>
        disabled={disabled}
        isItemEqualToValue={isItemEqualToValue}
        onValueChange={(nextValue, eventDetails) => {
          if (!eventDetails.isCanceled) {
            onChange?.(nextValue as TValue);
          }
        }}
        value={value}
      >
        <InputContainer
          cornerText={cornerText}
          error={fieldError}
          helpText={fieldHelpText}
          label={fieldLabel}
        >
          <BaseSelect.Trigger
            autoFocus={autoFocus}
            className={clsx(
              // Basic layout
              'group relative block w-full',

              // Background color + shadow applied to inset pseudo-element, so shadow blends with border
              'before:absolute before:inset-px before:bg-white',
              rounded
                ? 'before:rounded-[calc(var(--radius-full)-1px)]'
                : 'before:rounded-[calc(var(--radius-md)-1px)]',
              !inputGroup && 'before:shadow-sm',

              // Hide default focus styles
              'focus:outline-hidden',

              // Focus ring
              'after:pointer-events-none after:absolute after:inset-0 after:rounded-md after:ring-transparent after:ring-inset',
              !inputGroup && 'focus-visible:after:ring-2 focus-visible:after:ring-primary-500',

              // Disabled state
              'data-disabled:opacity-50 before:data-disabled:bg-gray-950/5 before:data-disabled:shadow-none'
            )}
            data-slot="control"
          >
            <span className={selectedOptionClasses}>
              <BaseSelect.Value className="block w-full min-w-0">
                {selectedValue => {
                  const selectedItem = items.find(item =>
                    isItemEqualToValue(item.value, selectedValue as TValue)
                  );

                  return selectedItem === undefined ? (
                    placeholder && (
                      <span className="block truncate text-gray-500">{placeholder}</span>
                    )
                  ) : (
                    <SelectOptionContainer>
                      {renderItemContent(selectedItem, { placement: 'value' })}
                    </SelectOptionContainer>
                  );
                }}
              </BaseSelect.Value>
            </span>
            <BaseSelect.Icon className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <LuChevronsUpDown className="size-5 text-gray-500 group-data-disabled:text-gray-300 sm:size-4 forced-colors:text-[CanvasText]" />
            </BaseSelect.Icon>
          </BaseSelect.Trigger>
        </InputContainer>

        <BaseSelect.Portal>
          <BaseSelect.Positioner
            align="start"
            alignItemWithTrigger
            className="pointer-events-none z-50 outline-none"
            sideOffset={4}
          >
            <BaseSelect.Popup
              className={clsx(
                // Anchor positioning
                'max-h-[min(var(--available-height),24rem)] max-w-(--available-width) min-w-[calc(var(--anchor-width)+1.75rem)] data-[side=none]:max-h-none',

                // Base styles
                'pointer-events-auto isolate w-max rounded-lg select-none',

                // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
                'origin-(--transform-origin) outline outline-transparent focus:outline-hidden',

                // Popover background
                'bg-white/75 backdrop-blur-xl',

                // Shadows
                'shadow-lg ring-1 ring-gray-950/10',

                // Transition
                'transition-[opacity,scale] duration-100 ease-out data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 motion-reduce:transition-none'
              )}
            >
              <BaseSelect.List className="scroll-py-1 overscroll-contain p-1">
                {items.map((item, index) => {
                  const itemKey =
                    item.key ??
                    (typeof item.value === 'string' ||
                    typeof item.value === 'number' ||
                    typeof item.value === 'bigint'
                      ? item.value
                      : item.value === null
                        ? 'null'
                        : index);

                  return (
                    <SelectOption
                      disabled={item.disabled}
                      key={itemKey}
                      label={item.textValue}
                      value={item.value}
                    >
                      {renderItemContent(item, { placement: 'option' })}
                    </SelectOption>
                  );
                })}
              </BaseSelect.List>
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
    </Field.Root>
  );
}

function renderItemContent<TValue>(item: SelectItem<TValue>, context: SelectRenderContext) {
  return typeof item.content === 'function' ? item.content(context) : item.content;
}

function SelectOptionContainer({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        // Base
        'flex w-full min-w-0 items-center',

        // Icons
        '*:data-[slot=icon]:group-data-highlighted/option:text-white forced-colors:*:data-[slot=icon]:text-[CanvasText] forced-colors:*:data-[slot=icon]:group-data-highlighted/option:text-[Canvas]',

        // Avatars
        '*:data-[slot=avatar]:-mx-0.5 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:size-5',

        className
      )}
    />
  );
}

type SelectOptionProps<TValue> = {
  children?: React.ReactNode;
  className?: string;
  value: TValue;
} & Omit<BaseSelect.Item.Props, 'children' | 'className' | 'value'>;

function SelectOption<TValue>({ children, className, ...props }: SelectOptionProps<TValue>) {
  return (
    <BaseSelect.Item
      {...props}
      className={clsx(
        // Basic layout
        'group/option grid cursor-default items-baseline gap-x-2 rounded-md',
        'grid-cols-[--spacing(5)_1fr] py-2.5 pr-3.5 pl-2 sm:grid-cols-[--spacing(4)_1fr] sm:py-1.5 sm:pr-3 sm:pl-1.5',

        // Typography
        'text-base/6 text-gray-950 sm:text-sm/6 forced-colors:text-[CanvasText]',

        // Focus
        'outline-hidden data-highlighted:bg-primary-600 data-highlighted:text-white',

        // Forced colors mode
        'forced-color-adjust-none forced-colors:data-highlighted:bg-[Highlight] forced-colors:data-highlighted:text-[HighlightText]',

        // Disabled
        'data-disabled:opacity-50'
      )}
    >
      <BaseSelect.ItemIndicator className="relative size-5 self-center text-current sm:size-4">
        <PiCheckBold className="size-full" />
      </BaseSelect.ItemIndicator>
      <BaseSelect.ItemText
        className={clsx(
          // Base
          'col-start-2 flex w-full min-w-0 items-center',

          // Icons
          '*:data-[slot=icon]:group-data-highlighted/option:text-white forced-colors:*:data-[slot=icon]:text-[CanvasText] forced-colors:*:data-[slot=icon]:group-data-highlighted/option:text-[Canvas]',

          // Avatars
          '*:data-[slot=avatar]:-mx-0.5 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:size-5',

          className
        )}
      >
        {children}
      </BaseSelect.ItemText>
    </BaseSelect.Item>
  );
}

export function SelectLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(className, 'ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0')}
    />
  );
}

export function SelectDetail({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'flex min-w-0 flex-1 justify-end overflow-hidden pl-2 text-gray-500 group-data-highlighted/option:text-white'
      )}
    >
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

export function SelectAvatar({
  className,
  ...props
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <span
      {...props}
      className={clsx('flex items-center justify-center', className)}
      data-slot="avatar"
    />
  );
}

export function SelectIcon({
  className,
  ...props
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <span
      {...props}
      className={clsx(
        'flex shrink-0 items-center justify-center text-gray-500 *:size-5 sm:*:size-4',
        className
      )}
      data-slot="icon"
    />
  );
}
